import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Brain,
  CheckCircle2,
  Edit3,
  Loader2,
  Plus,
  Search,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

// Type definitions
interface PhraseMapping {
  id: string;
  expectedPhrase: string;
  insuranceId: string;
  insuranceName: string | null;
  similarityScore?: number | null;
  usageCount: number;
  isAutoCreated?: boolean;
  updatedAt?: Date | string;
  createdAt?: Date | string;
  insuranceEhrId?: string | null;
}

export const Route = createFileRoute('/_protected/admin/insurance-phrase-mappings')({
  component: InsurancePhraseMappingsPage,
});

function InsurancePhraseMappingsPage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const router = useRouter();

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMappings, setSelectedMappings] = useState<string[]>([]);
  const [editDialog, setEditDialog] = useState<{
    isOpen: boolean;
    mapping: PhraseMapping | null;
  }>({ isOpen: false, mapping: null });
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    mapping: PhraseMapping | null;
  }>({ isOpen: false, mapping: null });
  const [addDialog, setAddDialog] = useState(false);

  // Form state for edit/add
  const [formData, setFormData] = useState({
    expectedPhrase: '',
    insuranceId: '',
  });

  const currentOrganizationId = organization?.id;

  // Redirect non-super admins
  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // Get tRPC utils for cache invalidation
  const utils = trpc.useUtils();

  // tRPC queries
  const autoCreatedQuery = trpc.admin.getAutoCreatedPhraseMappings.useQuery(
    { organizationId: currentOrganizationId || '' },
    { enabled: !!currentOrganizationId }
  );

  const allMappingsQuery = trpc.admin.getAllPhraseMappings.useQuery(
    { organizationId: currentOrganizationId || '' },
    { enabled: !!currentOrganizationId }
  );

  const insurancesQuery = trpc.admin.getAvailableInsurances.useQuery(
    { organizationId: currentOrganizationId || '' },
    { enabled: !!currentOrganizationId }
  );

  // tRPC mutations
  const deleteMutation = trpc.admin.deletePhraseMapping.useMutation({
    onSuccess: async () => {
      // Invalidate and refetch
      await utils.admin.getAutoCreatedPhraseMappings.invalidate();
      await utils.admin.getAllPhraseMappings.invalidate();
      setDeleteDialog({ isOpen: false, mapping: null });
    },
    onError: (error) => {
      toast.error(`Failed to delete phrase mapping: ${error.message}`);
    },
  });

  const updateMutation = trpc.admin.updatePhraseMapping.useMutation({
    onSuccess: async () => {
      // Invalidate and refetch
      await utils.admin.getAutoCreatedPhraseMappings.invalidate();
      await utils.admin.getAllPhraseMappings.invalidate();
      setEditDialog({ isOpen: false, mapping: null });
      toast.success('Phrase mapping updated successfully');
    },
    onError: (error) => {
      toast.error(`Failed to update phrase mapping: ${error.message}`);
    },
  });

  const createMutation = trpc.admin.createPhraseMapping.useMutation({
    onSuccess: async () => {
      // Invalidate and refetch
      await utils.admin.getAutoCreatedPhraseMappings.invalidate();
      await utils.admin.getAllPhraseMappings.invalidate();
      setAddDialog(false);
      setFormData({ expectedPhrase: '', insuranceId: '' });
      toast.success('Phrase mapping created successfully');
    },
    onError: (error) => {
      toast.error(`Failed to create phrase mapping: ${error.message}`);
    },
  });

  // Filter mappings based on search term
  const filteredAutoCreated = useMemo(() => {
    const mappings = autoCreatedQuery.data?.mappings;
    if (!mappings) return [];
    if (!searchTerm) return mappings;
    return mappings.filter(
      (mapping) =>
        mapping.expectedPhrase.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mapping.insuranceName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [autoCreatedQuery.data, searchTerm]);

  const filteredAllMappings = useMemo(() => {
    const mappings = allMappingsQuery.data?.mappings;
    if (!mappings) return [];
    if (!searchTerm) return mappings;
    return mappings.filter(
      (mapping) =>
        mapping.expectedPhrase.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mapping.insuranceName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allMappingsQuery.data, searchTerm]);

  // Loading states
  if (isAdminLoading || !orgLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  // Access denied
  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4 p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <ShieldAlert className="h-5 w-5" />
                Access Denied
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                Insurance Phrase Mappings is only available to super administrators.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleGoBack = () => {
    router.navigate({ to: '/admin' });
  };

  const handleSelectMapping = (mappingId: string) => {
    setSelectedMappings((prev) =>
      prev.includes(mappingId) ? prev.filter((id) => id !== mappingId) : [...prev, mappingId]
    );
  };

  const handleSelectAll = (mappings: typeof filteredAutoCreated) => {
    const allIds = mappings.map((m) => m.id);
    setSelectedMappings((prev) => (prev.length === allIds.length ? [] : allIds));
  };

  const getConfidenceColor = (similarity: number | null | undefined) => {
    if (!similarity) return 'bg-gray-100 text-gray-800';
    if (similarity >= 0.9) return 'bg-green-100 text-green-800';
    if (similarity >= 0.85) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getConfidenceLabel = (similarity: number | null | undefined) => {
    if (!similarity) return 'N/A';
    if (similarity >= 0.9) return 'High';
    if (similarity >= 0.85) return 'Medium';
    return 'Low';
  };

  const handleEditMapping = (mapping: PhraseMapping) => {
    setEditDialog({ isOpen: true, mapping });
    setFormData({
      expectedPhrase: mapping.expectedPhrase,
      insuranceId: mapping.insuranceId,
    });
  };

  const handleDeleteMapping = (mapping: PhraseMapping) => {
    setDeleteDialog({ isOpen: true, mapping });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.mapping && currentOrganizationId) {
      deleteMutation.mutate({
        id: deleteDialog.mapping.id,
      });
    }
  };

  const handleAddMapping = () => {
    setAddDialog(true);
    setFormData({ expectedPhrase: '', insuranceId: '' });
  };

  const handleSaveEdit = () => {
    if (!editDialog.mapping || !currentOrganizationId) return;
    updateMutation.mutate({
      id: editDialog.mapping.id,
      expectedPhrase: formData.expectedPhrase,
      insuranceId: formData.insuranceId,
    });
  };

  const handleSaveAdd = () => {
    if (!currentOrganizationId) return;
    createMutation.mutate({
      organizationId: currentOrganizationId,
      expectedPhrase: formData.expectedPhrase,
      insuranceId: formData.insuranceId,
    });
  };

  return (
    <div className="flex min-h-svh flex-col items-center gap-8 p-4">
      <div className="w-full max-w-7xl flex flex-col gap-6">
        {/* Super Admin Banner */}
        <div className="w-full bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg">
          <div className="flex items-center justify-center gap-2">
            <ShieldAlert className="h-5 w-5" />
            <span className="font-semibold">Super Administrator Access</span>
            <span className="text-red-200">•</span>
            <span className="text-sm">Insurance Phrase Mapping Management</span>
          </div>
        </div>

        {/* Page Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleGoBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Admin Hub
            </Button>
          </div>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-2">
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Brain className="h-8 w-8 text-primary" />
              Insurance Phrase Mappings
            </h1>
            <p className="text-muted-foreground">
              Review and manage auto-created insurance phrase mappings from semantic matching
            </p>
          </div>
        </div>

        {/* Organization Info */}
        <Card>
          <CardHeader>
            <CardTitle>Organization</CardTitle>
            <CardDescription>Managing phrase mappings for this organization</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold">
              {organization?.name ?? 'Unknown Organization'}
            </div>
            <div className="text-sm text-muted-foreground">ID: {currentOrganizationId}</div>
          </CardContent>
        </Card>

        {/* Main Content */}
        {currentOrganizationId ? (
          <>
            {/* Search Bar */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search phrase mappings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <Tabs defaultValue="auto-created" className="w-full">
              <TabsList className="grid w-full max-w-md grid-cols-2">
                <TabsTrigger value="auto-created" className="flex items-center gap-2">
                  <Brain className="h-4 w-4" />
                  Auto-Created
                  {autoCreatedQuery.data?.mappings && (
                    <span className="ml-1 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                      {autoCreatedQuery.data.mappings.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="all" className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  All Mappings
                  {allMappingsQuery.data?.mappings && (
                    <span className="ml-1 bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded-full">
                      {allMappingsQuery.data.mappings.length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="auto-created" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Brain className="h-5 w-5" />
                      Auto-Created Phrase Mappings
                    </CardTitle>
                    <CardDescription>
                      These mappings were automatically created from successful semantic matches.
                      Review for accuracy and approve or delete as needed.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {autoCreatedQuery.isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin" />
                        <span className="ml-2">Loading auto-created mappings...</span>
                      </div>
                    ) : autoCreatedQuery.error ? (
                      <div className="flex items-center justify-center py-8 text-destructive">
                        <AlertTriangle className="h-6 w-6 mr-2" />
                        <span>Error loading mappings: {autoCreatedQuery.error.message}</span>
                      </div>
                    ) : filteredAutoCreated.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">
                          {searchTerm
                            ? 'No matching auto-created mappings'
                            : 'No auto-created mappings found!'}
                        </p>
                        <p className="text-sm">
                          {searchTerm
                            ? 'Try adjusting your search terms.'
                            : 'Auto-created mappings will appear here when semantic matches occur.'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Bulk Actions */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={selectedMappings.length === filteredAutoCreated.length}
                              onCheckedChange={() => handleSelectAll(filteredAutoCreated)}
                            />
                            <span className="text-sm text-muted-foreground">
                              {selectedMappings.length > 0
                                ? `${selectedMappings.length} selected`
                                : 'Select all'}
                            </span>
                          </div>
                          {selectedMappings.length > 0 && (
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline">
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                                Approve Selected ({selectedMappings.length})
                              </Button>
                              <Button size="sm" variant="destructive">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Selected
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Recommendations Alert */}
                        {filteredAutoCreated.some((m) => (m.similarityScore || 0) < 0.85) && (
                          <Alert>
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                              Some mappings have low confidence scores (&lt; 0.85). Review these
                              carefully before approving.
                            </AlertDescription>
                          </Alert>
                        )}

                        {/* Mappings Table */}
                        <div className="border rounded-lg">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-12">
                                  <span className="sr-only">Select</span>
                                </TableHead>
                                <TableHead>Phrase</TableHead>
                                <TableHead>Insurance</TableHead>
                                <TableHead>Confidence</TableHead>
                                <TableHead>Usage Count</TableHead>
                                <TableHead>Created</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filteredAutoCreated.map((mapping) => (
                                <TableRow key={mapping.id}>
                                  <TableCell>
                                    <Checkbox
                                      checked={selectedMappings.includes(mapping.id)}
                                      onCheckedChange={() => handleSelectMapping(mapping.id)}
                                    />
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    "{mapping.expectedPhrase}"
                                  </TableCell>
                                  <TableCell>
                                    {mapping.insuranceName || 'Unknown Insurance'}
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      className={getConfidenceColor(mapping.similarityScore)}
                                      variant="secondary"
                                    >
                                      {getConfidenceLabel(mapping.similarityScore)}
                                      {mapping.similarityScore &&
                                        ` (${mapping.similarityScore.toFixed(3)})`}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <span>{mapping.usageCount}x</span>
                                      {mapping.usageCount > 10 && (
                                        <Badge variant="outline" className="text-xs">
                                          Popular
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    {mapping.createdAt
                                      ? new Date(mapping.createdAt).toLocaleDateString('en-US', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric',
                                        })
                                      : 'Legacy'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center gap-1 justify-end">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleEditMapping(mapping)}
                                      >
                                        <Edit3 className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDeleteMapping(mapping)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="all" className="mt-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>All Phrase Mappings</CardTitle>
                        <CardDescription>
                          Complete list of all phrase mappings (manual and auto-created)
                        </CardDescription>
                      </div>
                      <Button size="sm" onClick={handleAddMapping}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Manual Mapping
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {allMappingsQuery.isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin" />
                        <span className="ml-2">Loading all mappings...</span>
                      </div>
                    ) : allMappingsQuery.error ? (
                      <div className="flex items-center justify-center py-8 text-destructive">
                        <AlertTriangle className="h-6 w-6 mr-2" />
                        <span>Error loading mappings: {allMappingsQuery.error.message}</span>
                      </div>
                    ) : filteredAllMappings.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Brain className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">
                          {searchTerm ? 'No matching mappings found' : 'No phrase mappings found'}
                        </p>
                        <p className="text-sm">
                          {searchTerm
                            ? 'Try adjusting your search terms.'
                            : 'Create your first manual mapping to get started.'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="text-sm text-muted-foreground">
                          {allMappingsQuery.data?.mappings && (
                            <>
                              Total: {allMappingsQuery.data.mappings.length} mappings •{' '}
                              Auto-created:{' '}
                              {allMappingsQuery.data.mappings.filter((m) => m.isAutoCreated).length}{' '}
                              • Manual:{' '}
                              {
                                allMappingsQuery.data.mappings.filter((m) => !m.isAutoCreated)
                                  .length
                              }
                            </>
                          )}
                        </div>

                        <div className="border rounded-lg">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Phrase</TableHead>
                                <TableHead>Insurance</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Confidence</TableHead>
                                <TableHead>Usage Count</TableHead>
                                <TableHead>Last Updated</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filteredAllMappings.map((mapping) => (
                                <TableRow key={mapping.id}>
                                  <TableCell className="font-medium">
                                    "{mapping.expectedPhrase}"
                                  </TableCell>
                                  <TableCell>
                                    {mapping.insuranceName || 'Unknown Insurance'}
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      variant={mapping.isAutoCreated ? 'default' : 'secondary'}
                                    >
                                      {mapping.isAutoCreated ? 'Auto' : 'Manual'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    {mapping.similarityScore ? (
                                      <Badge
                                        className={getConfidenceColor(mapping.similarityScore)}
                                        variant="secondary"
                                      >
                                        {mapping.similarityScore.toFixed(3)}
                                      </Badge>
                                    ) : (
                                      <span className="text-muted-foreground">N/A</span>
                                    )}
                                  </TableCell>
                                  <TableCell>{mapping.usageCount}x</TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    {mapping.updatedAt
                                      ? new Date(mapping.updatedAt).toLocaleDateString('en-US', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric',
                                        })
                                      : 'Legacy'}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center gap-1 justify-end">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleEditMapping(mapping)}
                                      >
                                        <Edit3 className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDeleteMapping(mapping)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Brain className="h-16 w-16 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No Organization</h3>
              <p className="text-muted-foreground text-center max-w-md">
                You must be part of an organization to manage insurance phrase mappings.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) => setDeleteDialog({ isOpen: open, mapping: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Phrase Mapping</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the mapping for "
              {deleteDialog.mapping?.expectedPhrase}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ isOpen: false, mapping: null })}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditDialog({ isOpen: false, mapping: null });
            setFormData({ expectedPhrase: '', insuranceId: '' });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Phrase Mapping</DialogTitle>
            <DialogDescription>Update the phrase mapping configuration</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-phrase">Expected Phrase</Label>
              <Input
                id="edit-phrase"
                value={formData.expectedPhrase}
                onChange={(e) => setFormData({ ...formData, expectedPhrase: e.target.value })}
                placeholder="Enter the expected phrase"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-insurance">Insurance</Label>
              <Select
                value={formData.insuranceId}
                onValueChange={(value) => setFormData({ ...formData, insuranceId: value })}
              >
                <SelectTrigger id="edit-insurance">
                  <SelectValue placeholder="Select an insurance" />
                </SelectTrigger>
                <SelectContent>
                  {insurancesQuery.data?.map((insurance: { id: string; displayName: string }) => (
                    <SelectItem key={insurance.id} value={insurance.id}>
                      {insurance.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditDialog({ isOpen: false, mapping: null });
                setFormData({ expectedPhrase: '', insuranceId: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={
                updateMutation.isPending || !formData.expectedPhrase || !formData.insuranceId
              }
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Manual Mapping Dialog */}
      <Dialog
        open={addDialog}
        onOpenChange={(open) => {
          setAddDialog(open);
          if (!open) {
            setFormData({ expectedPhrase: '', insuranceId: '' });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Manual Phrase Mapping</DialogTitle>
            <DialogDescription>
              Create a new phrase mapping for insurance verification
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="add-phrase">Expected Phrase</Label>
              <Input
                id="add-phrase"
                value={formData.expectedPhrase}
                onChange={(e) => setFormData({ ...formData, expectedPhrase: e.target.value })}
                placeholder="Enter the expected phrase"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-insurance">Insurance</Label>
              <Select
                value={formData.insuranceId}
                onValueChange={(value) => setFormData({ ...formData, insuranceId: value })}
              >
                <SelectTrigger id="add-insurance">
                  <SelectValue placeholder="Select an insurance" />
                </SelectTrigger>
                <SelectContent>
                  {insurancesQuery.data?.map((insurance: { id: string; displayName: string }) => (
                    <SelectItem key={insurance.id} value={insurance.id}>
                      {insurance.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAddDialog(false);
                setFormData({ expectedPhrase: '', insuranceId: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAdd}
              disabled={
                createMutation.isPending || !formData.expectedPhrase || !formData.insuranceId
              }
            >
              {createMutation.isPending ? 'Creating...' : 'Create Mapping'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
